import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import Marquee from '../components/Marquee';
import MoreStories from '../components/MoreStories';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import { useLightbox } from '../hooks/useLightbox';

const galleryPhotos = [
  { src: '/floral-skyline.jpg', caption: 'A View Above the Bay', location: 'The Fairmont Penthouse Salons' },
  { src: '/floral-tablescape.jpg', caption: 'Sapphire & Gold', location: 'Navy Sequins, Mercury Votives, Blue Silk Napkins' },
  { src: '/floral-centerpiece.jpg', caption: 'Up Close, The Floral Corridor', location: 'Hydrangea, Garden Rose, Ranunculus — Branded by GRIFOLS' },
];

const FloralCorridorStory = () => {
  const [storyOpen, setStoryOpen] = useState(false);
  const storyRef = useRef(null);
  const lightbox = useLightbox();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleViewStory = () => {
    setStoryOpen(true);
    setTimeout(() => {
      storyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      <SEO
        title="The Floral Corridor — GRIFOLS Executive Forum | Dion Magee Events"
        description="A GRIFOLS executive forum dinner at The Fairmont San Francisco — navy sequins, white florals, and a corridor of garden roses. Corporate event design by Sir Dion Magee."
        path="/portfolio/the-floral-corridor"
        image="/floral-tablescape.jpg"
        type="article"
        article={{
          headline: 'The Floral Corridor — GRIFOLS Executive Forum',
          datePublished: '2014-09-04',
          section: 'Corporate Events',
        }}
      />
      {/* HERO */}
      <section className="relative h-[90vh] w-full overflow-hidden">
        <img
          src="/floral-tablescape.jpg"
          alt="The Floral Corridor — GRIFOLS Executive Forum"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-black/55" />

        <div className="absolute top-8 left-6 lg:top-12 lg:left-12 z-20">
          <Link
            to="/#portfolio"
            data-testid="back-to-portfolio"
            className="inline-flex items-center gap-3 text-white/80 hover:text-[#c9a96e] transition-colors text-[11px] tracking-[0.4em] uppercase"
          >
            <ArrowLeft size={16} />
            Back to Portfolio
          </Link>
        </div>

        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-20 lg:pb-28 px-6 text-center">
          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">
            A Corporate Story
          </p>
          <h1
            className="font-serif italic text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl leading-[1.05]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            The Floral Corridor
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mt-10" />
          <p className="text-white/70 text-xs sm:text-sm tracking-[0.3em] uppercase mt-6 max-w-3xl">
            GRIFOLS &nbsp;&middot;&nbsp; Leadership Plus One Executive Forum &nbsp;&middot;&nbsp; The Fairmont San Francisco
          </p>
          <p className="text-white/50 text-[10px] tracking-[0.4em] uppercase mt-3">
            September 4, 2014
          </p>
        </div>
      </section>

      {/* TOP GALLERY - 2 chapters */}
      <section className="bg-[#0a0a0a] py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-5">The Chapters</p>
            <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl">
              An Evening Above the Bay
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-7" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            <figure
              data-testid="floral-photo-0"
              onClick={() => lightbox.openAt(0)}
              className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
            >
              <img
                src="/floral-skyline.jpg"
                alt="A View Above the Bay"
                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
              <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter 01</p>
                <h3
                  className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  A View Above the Bay
                </h3>
                <p className="text-white/60 text-xs tracking-wider mt-2">The Fairmont Penthouse Salons</p>
              </figcaption>
            </figure>

            <figure
              data-testid="floral-photo-1"
              onClick={() => lightbox.openAt(1)}
              className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
            >
              <img
                src="/floral-tablescape.jpg"
                alt="Sapphire & Gold"
                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
              <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter 02</p>
                <h3
                  className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Sapphire &amp; Gold
                </h3>
                <p className="text-white/60 text-xs tracking-wider mt-2">Navy Sequins, Mercury Votives, Blue Silk Napkins</p>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* VIEW STORY CTA */}
      <section className="relative bg-[#0a0a0a] py-16 lg:py-24 px-6">
        <div className="pointer-events-none absolute -top-20 left-1/4 w-64 h-64 rounded-full bg-[#c9a96e]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 right-1/4 w-72 h-72 rounded-full bg-[#c9a96e]/10 blur-3xl" />

        <div className="relative max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>

          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">One Evening, One Boardroom in Bloom</p>
          <h3
            className="font-serif italic text-white text-3xl md:text-4xl lg:text-5xl mb-10 leading-snug"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Step into an executive dinner where strategy met design.
          </h3>

          <button
            type="button"
            onClick={handleViewStory}
            data-testid="view-story-button"
            className="group relative inline-block px-14 sm:px-20 py-6 sm:py-7 border border-[#c9a96e] bg-transparent overflow-hidden transition-colors duration-500 hover:bg-[#c9a96e]/10"
          >
            <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#e8d5a8]" />
            <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#e8d5a8]" />
            <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#e8d5a8]" />
            <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#e8d5a8]" />
            <span
              className="font-serif italic text-[#e8d5a8] text-2xl sm:text-3xl tracking-[0.2em] relative z-10"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              View Story
            </span>
          </button>

          <div className="flex items-center justify-center gap-6 mt-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>
        </div>
      </section>

      {/* STORY NARRATIVE */}
      <section
        ref={storyRef}
        className={`bg-[#0a0a0a] px-6 lg:px-12 overflow-hidden transition-all duration-700 ease-out ${
          storyOpen ? 'max-h-[6000px] opacity-100 py-20 lg:py-28' : 'max-h-0 opacity-0 py-0'
        }`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.55em] uppercase mb-6">The Story</p>
            <h2
              className="font-serif italic text-white text-4xl md:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              A Boardroom in Bloom
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-8" />
          </div>

          <div
            className="space-y-7 text-white/80 text-lg leading-[1.9] font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="floral-story-text"
          >
            <p className="first-letter:font-serif first-letter:italic first-letter:text-[#c9a96e] first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]">
              On a September evening high above the San Francisco Bay, the
              Fairmont&rsquo;s penthouse salons were transformed into a private
              theatre for global leadership. GRIFOLS &mdash; the international
              plasma medicine company &mdash; had gathered its executive forum:
              &ldquo;Leadership Plus One.&rdquo; One evening. One conversation. One
              setting worthy of both.
            </p>

            <p>
              The brief was singular. Create an atmosphere where decisions of
              consequence could be made over a table of beauty. The answer
              was sapphire &mdash; navy sequin linens that caught the late
              afternoon light like a midnight harbor &mdash; paired with hand-blown
              gold mercury votives that flickered like distant city lights
              below.
            </p>

            <p>
              At the center of every table stood a corridor of flowers: a
              low, abundant arrangement of white garden roses, hydrangea,
              ranunculus, and milky buds nestled in silver. It was not a
              centerpiece. It was an architecture &mdash; a small, fragrant wall
              that drew the eye, framed every conversation, and gave the
              room its name: The Floral Corridor.
            </p>

            <p>
              Guests entered through gilt doors into floor-to-ceiling glass
              and the entire skyline of San Francisco. The Transamerica
              Pyramid pierced the marine layer. Alcatraz floated quietly in
              the bay. And inside, the room grew warm with gold flame and the
              quiet talk of people who shape industries.
            </p>

            <p>
              Course by course, the kitchen at Fairmont served a duet to
              match the design: fennel-pollen seared scallops with cilantro
              emulsion; gathered summer greens with Fiscalini aged white
              cheddar; herb-marinated beef filet beside grilled Sonoma salmon;
              and at the end, a chocolate decadence cake softened with vanilla
              anglaise and a finishing pour of signature Fairmont coffee.
            </p>

            <p>
              By the time the last glass was lifted, leadership had spoken,
              partnerships had deepened, and the evening had become something
              more than dinner &mdash; a room of people transformed by the simple,
              civilizing power of design.
            </p>

            <p className="text-center text-[#c9a96e] italic pt-6">
              &mdash; Some boardroom decisions are made in a boardroom. The best ones are made between the candles.
            </p>
          </div>

          {/* BOTTOM GALLERY - 1 detail chapter */}
          <div className="grid grid-cols-1 gap-1 mt-20 max-w-3xl mx-auto">
            <figure
              data-testid="floral-photo-2"
              onClick={() => lightbox.openAt(2)}
              className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
            >
              <img
                src="/floral-centerpiece.jpg"
                alt="The Floral Corridor"
                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
              <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter 03</p>
                <h3
                  className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Up Close, The Floral Corridor
                </h3>
                <p className="text-white/60 text-xs tracking-wider mt-2">Hydrangea, Garden Rose, Ranunculus &mdash; Branded by GRIFOLS</p>
              </figcaption>
            </figure>
          </div>

          <div className="text-center mt-20">
            <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-8" />
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">An Original Production by</p>
            <p
              className="font-serif italic text-white text-3xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Dion Magee Events &amp; Weddings
            </p>
            <Link
              to="/#contact"
              data-testid="story-cta-enquire"
              className="inline-block mt-10 px-10 py-4 border border-[#c9a96e] text-[#e8d5a8] text-[11px] tracking-[0.4em] uppercase hover:bg-[#c9a96e] hover:text-black transition-colors duration-500"
            >
              Plan Your Corporate Event
            </Link>
          </div>
        </div>
      </section>

      <MoreStories currentSlug="the-floral-corridor" />
      <Marquee />
      <Footer />
      <Lightbox
        open={lightbox.open}
        onClose={lightbox.close}
        photos={galleryPhotos}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
      />
    </div>
  );
};

export default FloralCorridorStory;
